import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { semanticBrandingService, BrandDocument, Competitor } from '../services/semanticBranding';
import { ToastContext } from '../App';
import StepIndicator from '../components/Report/StepIndicator';
import InitialStep from '../components/Report/InitialStep';
import CompetitorsStep from '../components/Report/CompetitorsStep';
import AttributesStep from '../components/Report/AttributesStep';
import FinalReportStep from '../components/Report/FinalReportStep';
const Report = () => {
  const {
    uuid
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    addToast
  } = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [brandDocument, setBrandDocument] = useState<BrandDocument | null>(null);
  const [currentStep, setCurrentStep] = useState<'inicial' | 'concorrentes' | 'atributos' | 'analise'>('inicial');
  const [isNewDocument, setIsNewDocument] = useState(false);
  const [isPublicDocument, setIsPublicDocument] = useState(false);
  // Check if this is a new document from URL state
  useEffect(() => {
    if (location.state && location.state.isNewDocument) {
      setIsNewDocument(true);
    }
  }, [location.state]);
  // Fetch brand document data
  useEffect(() => {
    const fetchBrandDocument = async () => {
      if (!uuid) return;
      setIsLoading(true);
      setError('');
      try {
        // First try to get the document without auth to check if it's public
        try {
          const publicResponse = await semanticBrandingService.getPublicBrandDocument(uuid);
          if (publicResponse && publicResponse.brandDocument) {
            setBrandDocument(publicResponse.brandDocument);
            setIsPublicDocument(true);
            // If document is in initial step, automatically go to competitors step
            if (publicResponse.brandDocument.step === 'inicial') {
              setCurrentStep('concorrentes');
            } else {
              // Set step based on document status
              const docStep = publicResponse.brandDocument.step;
              // Handle both "analise" and "concluido" as final step
              if (docStep === 'concluido') {
                setCurrentStep('analise');
              } else {
                setCurrentStep(docStep);
              }
            }
            setIsLoading(false);
            return;
          }
        } catch (publicError) {
          // If public access fails, try authenticated access
          console.log('Document is not public, trying authenticated access');
        }
        // If we reach here, try authenticated access
        const response = await semanticBrandingService.getBrandDocument(uuid);
        setBrandDocument(response.brandDocument);
        // If document is in initial step, automatically go to competitors step
        if (response.brandDocument.step === 'inicial') {
          setCurrentStep('concorrentes');
        } else {
          // Set step based on document status
          const docStep = response.brandDocument.step;
          // Handle both "analise" and "concluido" as final step
          if (docStep === 'concluido') {
            setCurrentStep('analise');
          } else {
            setCurrentStep(docStep);
          }
        }
      } catch (error) {
        console.error('Failed to fetch brand document:', error);
        setError(error.response?.data?.message || 'Erro ao carregar dados do relatório. Verifique se o ID é válido.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchBrandDocument();
  }, [uuid, isNewDocument]);
  // Handle step transitions
  const handleNextStep = (step: 'inicial' | 'concorrentes' | 'atributos' | 'analise') => {
    if (step === 'inicial') {
      setCurrentStep('concorrentes');
    } else if (step === 'concorrentes') {
      setCurrentStep('atributos');
    } else if (step === 'atributos') {
      setCurrentStep('analise');
    }
  };
  const handlePreviousStep = (step: 'concorrentes' | 'atributos' | 'analise') => {
    if (step === 'concorrentes') {
      setCurrentStep('inicial');
    } else if (step === 'atributos') {
      setCurrentStep('concorrentes');
    } else if (step === 'analise') {
      setCurrentStep('atributos');
    }
  };
  // Update brand document info
  const handleUpdateBrandInfo = (marca: string, url: string) => {
    if (brandDocument) {
      setBrandDocument({
        ...brandDocument,
        marca,
        url
      });
      addToast('success', 'Informações da marca atualizadas com sucesso!');
    }
  };
  // Update competitors
  const handleUpdateCompetitors = (competitors: Competitor[]) => {
    if (brandDocument) {
      setBrandDocument({
        ...brandDocument,
        step: 'concorrentes',
        metadata: {
          ...brandDocument.metadata,
          concorrentesSelecionados: competitors
        }
      });
      addToast('success', 'Concorrentes salvos com sucesso!');
      handleNextStep('concorrentes');
    }
  };
  // Get favicon from URL
  const getFavicon = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch (e) {
      return `https://www.google.com/s2/favicons?domain=example.com&sz=128`;
    }
  };
  if (isLoading) {
    return <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]" data-id="element-1348" data-mp="r95asd74o3a2">
        <Loader2 size={32} className="animate-spin text-[#1649ff] mb-4" data-id="element-1349" data-mp="di06gxstemte" />
        <p className="text-gray-900" data-id="element-1350" data-mp="hiiyosymduw4">Carregando relatório...</p>
      </div>;
  }
  if (error) {
    return <div className="container mx-auto px-4 py-12" data-id="element-1351" data-mp="157v89txv04yy">
        <div className="max-w-2xl mx-auto p-6 bg-red-50 rounded-lg flex items-start" data-id="element-1352" data-mp="1a0oaaa1y0z7c">
          <AlertCircle size={24} className="text-red-500 mr-4 flex-shrink-0 mt-1" data-id="element-1353" data-mp="1jw515i2ovv1m" />
          <div data-id="element-1354" data-mp="1fu0409d18ga8">
            <h2 className="text-xl font-semibold text-red-700 mb-2" data-id="element-1355" data-mp="1fvl2ub6dqldw">
              Erro ao carregar relatório
            </h2>
            <p className="text-red-600 mb-4" data-id="element-1356" data-mp="1atlyoz2tfkfw">{error}</p>
          </div>
        </div>
      </div>;
  }
  if (!brandDocument) {
    return <div className="container mx-auto px-4 py-12" data-id="element-1357" data-mp="10x0u2eupkbcl">
        <div className="max-w-2xl mx-auto p-6 bg-yellow-50 rounded-lg" data-id="element-1358" data-mp="10yk7lxozc8ao">
          <h2 className="text-xl font-semibold text-yellow-700 mb-2" data-id="element-1359" data-mp="w5hm8cucw55k">
            Relatório não encontrado
          </h2>
          <p className="text-yellow-600 mb-4" data-id="element-1360" data-mp="s5h1kagnyxq2">
            Não foi possível encontrar o relatório com o ID fornecido.
          </p>
        </div>
      </div>;
  }
  return <div className="container mx-auto px-4 sm:px-6 py-8 w-full" style={{
    maxWidth: '100%'
  }} data-id="element-1361" data-mp="i5q4c10py4ma">
      <div className="mx-auto" data-id="element-1362" data-mp="d8gggpsza10k">
        {/* Only show brand info and step indicator if not in analysis step */}
        {currentStep !== 'analise' && <>
            <h1 className="text-2xl font-bold mb-2 flex items-center text-gray-900" data-id="element-1363" data-mp="81t94w49xq07">
              {brandDocument.url && <img src={getFavicon(brandDocument.url)} alt={`${brandDocument.marca} favicon`} className="w-6 h-6 mr-2" onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
          }} data-id="element-1364" data-mp="d2p9x0tbww1e" />}
              Análise Semântica: {brandDocument.marca}
            </h1>
            <p className="text-gray-800 mb-6" data-id="element-1365" data-mp="3v9n6kip2nyev">
              {currentStep === 'inicial' ? 'Defina as informações básicas da sua marca' : currentStep === 'concorrentes' ? 'Selecione os concorrentes para análise comparativa' : currentStep === 'atributos' ? 'Defina os atributos para análise da sua marca' : 'Relatório final de análise semântica'}
            </p>
            <StepIndicator currentStep={currentStep} data-id="element-1366" data-mp="urasperif1bq" />
          </>}
        {currentStep === 'inicial' && <InitialStep documentId={brandDocument.id} marca={brandDocument.marca} url={brandDocument.url} onUpdate={handleUpdateBrandInfo} onNextStep={() => handleNextStep('inicial')} data-id="element-1367" data-mp="pq3mhk50ma76" />}
        {currentStep === 'concorrentes' && <CompetitorsStep documentId={brandDocument.id} existingSuggestions={brandDocument.metadata?.concorrentesSugeridos || []} existingSelections={brandDocument.metadata?.concorrentesSelecionados || []} onSave={handleUpdateCompetitors} onNextStep={() => handleNextStep('concorrentes')} onPreviousStep={() => handlePreviousStep('concorrentes')} autoFetchSuggestions={brandDocument.step === 'inicial'} data-id="element-1368" data-mp="1dj3k54qxkehc" />}
        {currentStep === 'atributos' && <AttributesStep documentId={brandDocument.id} onNextStep={() => handleNextStep('atributos')} onPreviousStep={() => handlePreviousStep('atributos')} data-id="element-1369" data-mp="1s6298t2bewsw" />}
        {currentStep === 'analise' && <FinalReportStep documentId={brandDocument.id} onPreviousStep={() => handlePreviousStep('analise')} data-id="element-1370" data-mp="1n5elqhdaoy3f" />}
      </div>
    </div>;
};
export default Report;